import { Injectable } from '@angular/core';
import { catchError, map, merge, Observable, of, switchMap, toArray } from 'rxjs';

import {
  AllPerimeterDto,
  Channel,
  ChannelIdAndRolesDto,
  ContactEntryElsQuery,
  ElasticSearchService,
  ElsQueryBaseParams,
  HttpService,
  RECORD_FACADE_PREFIX,
  TileId,
  unique,
  USER_FACADE_PREFIX,
} from '@contact/upgrade';

@Injectable({
  providedIn: 'root',
})
export class HistoryService {
  constructor(private readonly http: HttpService, private readonly elasticSearchService: ElasticSearchService) {}

  search(
    payload: {
      firstname?: string;
      lastname?: string;
      companyname?: string;
      uri?: string;
      channel?: Channel;
      campaign?: string;
      csr?: string;
      beginDate?: string;
      endDate?: string;
    } & ElsQueryBaseParams
  ) {
    return this.getPerimeters().pipe(
      switchMap(({ flowsUuid, excludedSkillsUuid, domainUuidList, teamsUuid }) =>
        this.elasticSearchService.search(
          ContactEntryElsQuery.buildSearchWithVisibility({
            ...payload,
            flowIds: flowsUuid,
            excludeSkillIds: excludedSkillsUuid,
            domainIds: domainUuidList,
            teamIds: teamsUuid,
          })
        )
      )
    );
  }

  isRecordAvailable(technicalId: string): Observable<boolean> {
    return this.http
      .cache()
      .withParams({ param: [technicalId] }) // fix to cache requests
      .post(`${RECORD_FACADE_PREFIX}/getChannelStatus`, technicalId, { responseType: 'text' })
      .pipe(
        map(data => data === 'AVAILABLE' || data === 'OUT_STRATEGY' || data === 'RESTRICTED'),
        catchError(() => of(false))
      );
  }

  getRecordStatus(technicalId: string): Observable<string> {
    const data: ChannelIdAndRolesDto = {
      channelId: technicalId,
      roles: ['HISTORIZATION'],
    };
    return this.http
      .cache()
      .withParams({ param: [technicalId] }) // fix to cache requests
      .post(`${RECORD_FACADE_PREFIX}/getChannelStatusForRoles`, data, { responseType: 'text' })
      .pipe(
        map(data => (data === 'AVAILABLE' || data === 'OUT_STRATEGY' || data === 'RESTRICTED' ? data : 'UNKNOWN')),
        catchError(() => of('UNKNOWN'))
      );
  }

  getPerimeters(): Observable<AllPerimeterDto> {
    return this.http.cache().withUrlParams([TileId.HISTORIZATION]).get<AllPerimeterDto>(`${USER_FACADE_PREFIX}/getPerimeters`);
  }

  getDomainsOfPerimeter(): Observable<string[]> {
    return this.http.cache().withUrlParams([TileId.HISTORIZATION]).get<string[]>(`${USER_FACADE_PREFIX}/getDomainsOfPerimeter`);
  }

  getRepositories(domainId: string): Observable<string> {
    return this.http
      .withHeaders({
        'Content-Type': 'text/plain; charset=UTF-8',
        Accept: 'text/plain; charset=UTF-8',
      })
      .withUrlParams([domainId])
      .get('public/tools/getCustomerRepositoryNameFromDomain', { responseType: 'text' });
  }

  getAllRepositories(): Observable<string[]> {
    return this.getDomainsOfPerimeter().pipe(
      switchMap(domainIds =>
        merge(...domainIds.map(domainId => this.getRepositories(domainId))).pipe(
          toArray(),
          map(repositories => unique(repositories))
        )
      )
    );
  }
}
